import * as consts from 'consts/consts';
import { FormattedMessage, type IntlShape } from 'react-intl';
import { getEngagementSurveyComponents } from 'views/EngagementSurvey/EngagementSurvey';
import { CARD_TYPES } from '../../Dashboards/CardRowDashboard';

export const renderImpactRows = ({
  formatMessage,
  organizationName,
  ratingRequired,
  shouldShowInfluence,
  shouldShowGoldStars,
  shouldShowHeadsUps,
  colorConfig,
  getPowerLawLink,
}) => {
  return [
    [
      {
        id: 'gold-stars-header',
        type: CARD_TYPES.HEADER,
        title: formatMessage({
          id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.gold_stars_header.title',
          defaultMessage: 'Outstanding impact',
        }),
        datakey: 'gold_stars_header',
      },
    ],
    [
      {
        type: CARD_TYPES.POWER_LAW_CARD,
        title: (
          <span>
            <FormattedMessage
              id="app.views.widgets.dashboards.takeaways_dashboard.talent.title"
              defaultMessage="At {organizationName}, recognition for outstanding impact follows a <powerlawlink></powerlawlink>."
              values={{
                organizationName,
                powerlawlink: () => getPowerLawLink(0),
              }}
            />
          </span>
        ),
        color: colorConfig.gold_star,
        fieldSingular: formatMessage({
          id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.talent.field_singular',
          defaultMessage: 'outstanding impact',
        }),
        fieldPlural: formatMessage({
          id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.talent.field_plural',
          defaultMessage: 'outstanding impacts',
        }),
        datakey: 'talent',
      },
    ],
    [
      {
        contentId: 'top-performers',
        type: CARD_TYPES.LEADERBOARD_CARD,
        title: formatMessage(
          {
            id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.top_performers.title',
            defaultMessage: "{organizationName}'s top recognized performers",
          },
          { organizationName }
        ),
        datakey: 'top_performers',
        showRating: ratingRequired,
        shouldShowInfluence,
        shouldShowGoldStars,
        shouldShowHeadsUps,
      },
    ],
    [
      {
        type: CARD_TYPES.SCATTER_PLOT,
        title: `vs tenure`,
        datakey: 'breakdowns',
        field: 'individual',
        metric: consts.PERFORMANCE_METRICS[2],
        shouldShowInfluence,
        shouldShowGoldStars,
        shouldShowHeadsUps,
        beforeContent: (
          <div>
            <FormattedMessage
              id="app.views.widgets.dashboards.takeaways_dashboard.individual_breakdowns.before_content"
              defaultMessage="
          While outstanding impact tends to go up with tenure, managers are recognized for
          outstanding impact less frequently than individual contributors.
        "
            />
          </div>
        ),
      },
    ],
    [
      {
        type: CARD_TYPES.CANDLESTICK_CHART,
        datakey: 'breakdowns',
        color: colorConfig.gold_star,
        metric: consts.PERFORMANCE_METRICS[2],
        beforeContent: (
          <div>
            <FormattedMessage
              id="app.views.widgets.dashboards.takeaways_dashboard.breakdowns.before_content"
              defaultMessage="Hover on a candlestick to see its distribution details."
            />
          </div>
        ),
      },
    ],
    [
      {
        type: CARD_TYPES.BREAKDOWN_CHART,
        breakdownType: 'gold_stars',
        color: colorConfig.gold_star,
        tenureColor: colorConfig.tenure,
        title: formatMessage(
          {
            id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.gold_stars_average.title',
            defaultMessage: 'Average outstanding impact and tenure by{space}',
          },
          { space: ' ' }
        ),
        datakey: 'breakdowns',
        beforeContent: (
          <div>
            <FormattedMessage
              id="app.views.widgets.dashboards.takeaways_dashboard.breakdowns_gold_stars.before_content"
              defaultMessage="
          Keep in mind these averages when assessing specific individuals.
        "
            />
          </div>
        ),
      },
    ],
  ];
};

export const renderInfluenceRows = ({
  formatMessage,
  organizationName,
  getPowerLawLink,
  colorConfig,
  //   I think we can remove this
  shouldShowInfluence,
  shouldShowGoldStars,
  shouldShowHeadsUps,
}) => {
  return [
    [
      {
        id: 'influence-header',
        type: CARD_TYPES.HEADER,
        title: formatMessage({
          id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.influence.title',
          defaultMessage: 'Influence',
        }),
        datakey: 'influence_header',
      },
    ],
    [
      {
        type: CARD_TYPES.POWER_LAW_CARD,
        title: (
          <span>
            <FormattedMessage
              id="app.views.widgets.dashboards.takeaways_dashboard.influence.title"
              defaultMessage="At {organizationName}, influence also follows a <powerlawlink></powerlawlink>."
              values={{
                organizationName,
                powerlawlink: () => getPowerLawLink(2),
              }}
            />
          </span>
        ),
        color: colorConfig.influence,
        fieldSingular: formatMessage({
          id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.influence.field_singular',
          defaultMessage: 'influence',
        }),
        fieldPlural: formatMessage({
          id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.influence.field_plural',
          defaultMessage: 'influence',
        }),
        datakey: 'influence',
      },
    ],
    [
      {
        type: CARD_TYPES.TRIPLE_LEADERBOARD_CARD,
        contentId: 'top-influencers',
        title: formatMessage(
          {
            id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.callouts.title',
            defaultMessage: "{organizationName}'s influencer callouts",
          },
          { organizationName }
        ),
        datakey: 'top_influencers',
        shouldShowInfluence,
        shouldShowGoldStars,
        shouldShowHeadsUps,
      },
    ],
    [
      {
        type: CARD_TYPES.SCATTER_PLOT,
        title: formatMessage({
          id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.vs_tenure.title',
          defaultMessage: 'vs tenure',
        }),
        datakey: 'breakdowns',
        field: 'individual',
        metric: consts.PERFORMANCE_METRICS[0],
        shouldShowInfluence,
        shouldShowGoldStars,
        shouldShowHeadsUps,
        beforeContent: (
          <div>
            <FormattedMessage
              id="app.views.widgets.dashboards.takeaways_dashboard.individual_breakdown.before_content"
              defaultMessage="
                Influence tends to go up with tenure, and managers tend to
                have more influence than individual contributors.
              "
            />
          </div>
        ),
      },
    ],
    [
      {
        type: CARD_TYPES.CANDLESTICK_CHART,
        datakey: 'breakdowns',
        color: colorConfig.influence,
        metric: consts.PERFORMANCE_METRICS[0],
        beforeContent: (
          <div>
            <FormattedMessage
              id="app.views.widgets.dashboards.takeaways_dashboard.breakdowns_influence.before_content"
              defaultMessage="
                Hover on a candlestick to see its distribution details.
              "
            />
          </div>
        ),
      },
    ],
    [
      {
        type: CARD_TYPES.BREAKDOWN_CHART,
        breakdownType: 'influence',
        color: colorConfig.influence,
        tenureColor: colorConfig.tenure,
        title: formatMessage(
          {
            id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.influence_breakdown.title',
            defaultMessage: 'Average influence and tenure by{space}',
          },
          { space: ' ' }
        ),
        datakey: 'breakdowns',
        beforeContent: (
          <div>
            <FormattedMessage
              id="app.views.widgets.dashboards.takeaways_dashboard.influence.before_content"
              defaultMessage="
                Keep in mind these averages when assessing specific
                individuals.
              "
            />
          </div>
        ),
      },
    ],
  ];
};
export const renderInfluenceAndInpactRows = ({
  formatMessage,
  shouldShowInfluence,
  shouldShowGoldStars,
  shouldShowHeadsUps,
}) => {
  return [
    [
      {
        id: 'gold-stars-and-influence-header',
        type: CARD_TYPES.HEADER,
        title: formatMessage({
          id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.positive_vs_tenure.title',
          defaultMessage: 'Positive recognition vs tenure',
        }),
        datakey: 'gold_stars_and_influence_header',
      },
    ],
    [
      {
        type: CARD_TYPES.SCATTER_PLOT,
        title: formatMessage({
          id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.vs_tenure.title',
          defaultMessage: 'vs tenure',
        }),
        datakey: 'breakdowns',
        field: 'individual',
        metric: 'influence_gold_stars',
        shouldShowInfluence,
        shouldShowGoldStars,
        shouldShowHeadsUps,
        beforeContent: (
          <div>
            <FormattedMessage
              id="app.views.widgets.dashboards.takeaways_dashboard.influence_gold_stars.before_content"
              defaultMessage="
                By adding influence and outstanding impact together, we see how the
                network recognizes positive recognition in all forms.
              "
            />
          </div>
        ),
      },
    ],
  ];
};

export const renderEngagementRedirect = ({
  hasEngagementSurveyElementsToDisplay,
}) => {
  if (!hasEngagementSurveyElementsToDisplay) {
    return [];
  }
  return [
    [
      {
        id: 'engagement-redirect-header',
        type: CARD_TYPES.HEADER,
        title: null,
        datakey: 'engagement_redirect_header',
      },
    ],
    [
      {
        id: 'engagement-redirect',
        type: CARD_TYPES.SIMPLE_CARD,
        title: null,
        datakey: 'engagement_redirect',
      },
    ],
  ];
};

export const renderUnifiedSummaryGenerationRow = ({
  campaignId,
  reportItemId,
  showEnpsSummary,
  showEngagementSurveySummary,
}) => {
  // Only render if at least one summary type is enabled
  if (!showEnpsSummary && !showEngagementSurveySummary) {
    return [];
  }
  return [
    [
      {
        id: 'unified-summary-generation-card',
        type: CARD_TYPES.UNIFIED_SUMMARY_GENERATION,
        campaignId,
        reportItemId,
        showEnpsSummary,
        showEngagementSurveySummary,
      },
    ],
  ];
};

export const renderENpsRows = ({
  formatMessage,
  shouldShowEmployeeNPS,
  shouldShowEngagementSurveyPlaceholder,
  campaignHasENPSEnabled,
  organizationId,
  campaignId,
  reportItemId,
}) => {
  return shouldShowEmployeeNPS
    ? [
        [
          {
            id: 'employee-nps-header',
            type: CARD_TYPES.HEADER,
            title: null,
            datakey: 'employee_nps_header',
          },
        ],
        [
          {
            id: 'employee-nps-card',
            type: CARD_TYPES.EMPLOYEE_NPS,
            title: '',
            datakey: 'employee_nps',
            organizationId: organizationId,
            campaignId: campaignId,
            reportItemId: reportItemId,
          },
        ],
      ]
    : [
        ...(shouldShowEngagementSurveyPlaceholder && campaignHasENPSEnabled
          ? [
              [
                {
                  id: 'employee-nps-header',
                  type: CARD_TYPES.HEADER,
                  title: formatMessage({
                    id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.enps.title',
                    defaultMessage: 'Employee NPS',
                  }),
                  datakey: 'employee_nps_header',
                },
              ],
              [
                {
                  type: CARD_TYPES.SIMPLE_CARD,
                  title: formatMessage({
                    id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.enps.title',
                    defaultMessage: 'Employee NPS',
                  }),
                  datakey: 'enps_placeholder',
                },
              ],
            ]
          : []),
      ];
};

export const renderEngagementSurveyRows = ({
  campaignHasEngagementSurvey,
  formatMessage,
  campaign,
  shouldShowEngagementSurveySummary,
  shouldShowEngagementSurveyPlaceholder,
  campaignHasEngagementSurveyEnabled,
  engagementSurveyData,
  engagementSurveySummaryData,
  campaignReportItemId,
}) => {
  return campaignHasEngagementSurvey
    ? [
        [
          {
            id: 'engagement-survey-header',
            type: CARD_TYPES.HEADER,
            title: formatMessage({
              id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.engagement_survey.title',
              defaultMessage: 'Engagement survey',
            }),
            datakey: 'engagement_survey_header',
          },
        ],
        // TODO: let it out...
        getEngagementSurveyComponents(
          formatMessage,
          campaign,
          engagementSurveyData,
          null,
          campaignReportItemId
        ),
        shouldShowEngagementSurveySummary
          ? [
              {
                type: CARD_TYPES.ENGAGEMENT_SURVEY_SUMMARY,
                summary: engagementSurveySummaryData,
                campaignName: campaign?.name,
                campaignId: campaign?.id,
                reportItemId: campaignReportItemId,
              },
            ]
          : [],
      ]
    : [
        ...(shouldShowEngagementSurveyPlaceholder &&
        campaignHasEngagementSurveyEnabled
          ? [
              [
                {
                  id: 'engagement-survey-header',
                  type: CARD_TYPES.HEADER,
                  title: formatMessage({
                    id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.engagement_survey.title',
                    defaultMessage: 'Engagement survey',
                  }),
                  datakey: 'engagement_survey_header',
                },
              ],
              [
                {
                  type: CARD_TYPES.SIMPLE_CARD,
                  title: formatMessage({
                    id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.engagement_survey.title',
                    defaultMessage: 'Engagement survey',
                  }),
                  datakey: 'engagegement_survey_placeholder',
                },
              ],
            ]
          : []),
      ];
};

export const renderTalentMatrixRows = () => {
  return [
    [
      {
        id: 'talent-matrix-header',
        type: CARD_TYPES.HEADER,
        datakey: 'talent_matrix_header',
      },
    ],
  ];
};

const getSkillCloudTitle = ({
  shouldShowGoldStars,
  shouldShowHeadsUps,
  formatMessage,
}: {
  shouldShowGoldStars: boolean;
  shouldShowHeadsUps: boolean;
  formatMessage: IntlShape['formatMessage'];
}): string => {
  if (shouldShowGoldStars && shouldShowHeadsUps) {
    return formatMessage({
      id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.gold_stars_heads_up.title',
      defaultMessage: 'Outstanding impact and heads ups',
    });
  } else if (shouldShowGoldStars) {
    return formatMessage({
      id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.gold_stars.title',
      defaultMessage: 'Outstanding impact',
    });
  } else {
    return formatMessage({
      id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.heads_up.title',
      defaultMessage: 'Heads ups',
    });
  }
};

const getSkillCloudBeforeContent = ({
  shouldShowGoldStars,
  shouldShowHeadsUps,
}: {
  shouldShowGoldStars: boolean;
  shouldShowHeadsUps: boolean;
}): JSX.Element => {
  if (shouldShowGoldStars && shouldShowHeadsUps) {
    return (
      <p>
        <FormattedMessage
          id="app.views.widgets.dashboards.takeaways_dashboard.gold_stars_and_heads_ups.before_content"
          defaultMessage="<bold>These were tagged when recognizing outstanding impact and heads ups.</bold> They represent collective strengths of your top talent and collective areas of improvement for those that need additional support."
          values={{
            bold: (text) => <span className="fw-bold">{text}</span>,
          }}
        />
      </p>
    );
  } else if (shouldShowGoldStars) {
    return (
      <p>
        <FormattedMessage
          id="app.views.widgets.dashboards.takeaways_dashboard.gold_stars_only.before_content"
          defaultMessage="<bold>These were tagged when recognizing outstanding impact.</bold> They represent collective strengths of your top talent."
          values={{
            bold: (text) => <span className="fw-bold">{text}</span>,
          }}
        />
      </p>
    );
  } else {
    return (
      <p>
        <FormattedMessage
          id="app.views.widgets.dashboards.takeaways_dashboard.heads_ups_only.before_content"
          defaultMessage="<bold>These were tagged when recognizing heads ups.</bold> They represent collective areas of improvement for those that need additional support."
          values={{
            bold: (text) => <span className="fw-bold">{text}</span>,
          }}
        />
      </p>
    );
  }
};

export const renderCompetenciesRows = ({
  skillsFromSelfSkillOwners,
  skillsFromManagerSkillOwners,
  skillsFromOthersSkillOwners,
  skillsFromInfluenceSkillOwners,
  shouldShowGoldStars = true,
  shouldShowHeadsUps = true,
  shouldShowInfluence = true,
  formatMessage,
}) => {
  return [
    [
      {
        id: 'skills-and-behaviors-header',
        type: CARD_TYPES.HEADER,
        title: formatMessage({
          id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.skills_and_behaviors.title',
          defaultMessage: 'Skills and behaviors',
        }),
        datakey: 'skills_and_behaviors_header',
      },
    ],
    [
      {
        type: CARD_TYPES.SKILL_CLOUDS_CARD,
        title: formatMessage({
          id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.self_reflections.title',
          defaultMessage: 'Self reflections',
        }),
        beforeContent: (
          <p>
            <FormattedMessage
              id="app.views.widgets.dashboards.takeaways_dashboard.self_reflection.before_content"
              defaultMessage="<bold>These were mentioned in self reflections.</bold> When considering trainings, courses, and mentorship programs, consider these areas to focus on."
              values={{
                bold: (text) => <span className="fw-bold">{text}</span>,
              }}
            />
          </p>
        ),
        datakey: 'self_skills_and_behaviors',
        skillOwners: skillsFromSelfSkillOwners,
      },
    ],
    [
      {
        type: CARD_TYPES.SKILL_CLOUDS_CARD,
        title: formatMessage({
          id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.manager_reviews.title',
          defaultMessage: 'Manager reviews',
        }),
        beforeContent: (
          <p>
            <FormattedMessage
              id="app.views.widgets.dashboards.takeaways_dashboard.manager_reviews.before_content"
              defaultMessage="<bold>These were mentioned by managers in their reviews.</bold> When considering training and other programming for people
        managers, these are areas for which guidance and support would be
        helpful."
              values={{
                bold: (text) => <span className="fw-bold">{text}</span>,
              }}
            />
          </p>
        ),
        datakey: 'manager_review_skills_and_behaviors',
        skillOwners: skillsFromManagerSkillOwners,
      },
    ],
    ...(shouldShowGoldStars || shouldShowHeadsUps
      ? [
          [
            {
              type: CARD_TYPES.SKILL_CLOUDS_CARD,
              title: getSkillCloudTitle({
                shouldShowGoldStars,
                shouldShowHeadsUps,
                formatMessage,
              }),
              beforeContent: getSkillCloudBeforeContent({
                shouldShowGoldStars,
                shouldShowHeadsUps,
              }),
              datakey: 'gold_star_heads_up_skills_and_behaviors',
              skillOwners: skillsFromOthersSkillOwners,
            },
          ],
        ]
      : []),
    ...(shouldShowInfluence
      ? [
          [
            {
              type: CARD_TYPES.SKILL_CLOUDS_CARD,
              title: formatMessage({
                id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.competencies_influence.title',
                defaultMessage: 'Influence',
              }),
              beforeContent: (
                <p>
                  <FormattedMessage
                    id="app.views.widgets.dashboards.takeaways_dashboard.competencies_influence.before_content"
                    defaultMessage="<bold>These were tagged when recognizing who people go to for help, advice, energy, or motivation.</bold> They represent collective strengths of your organization as seen by the network."
                    values={{
                      bold: (text) => <span className="fw-bold">{text}</span>,
                    }}
                  />
                </p>
              ),
              datakey: 'influence_skills_and_behaviors',
              skillOwners: skillsFromInfluenceSkillOwners,
            },
          ],
        ]
      : []),
  ];
};

export const renderConcernsRows = ({
  formatMessage,
  organizationName,
  ratingRequired,
  shouldShowInfluence,
  shouldShowGoldStars,
  shouldShowHeadsUps,
  colorConfig,
}) => {
  return [
    [
      {
        id: 'concerns-header',
        type: CARD_TYPES.HEADER,
        title: formatMessage({
          id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.concerns.title',
          defaultMessage: 'Concerns',
        }),
        datakey: 'concerns_header',
      },
    ],
    [
      {
        contentId: 'top-concerns',
        type: CARD_TYPES.LEADERBOARD_CARD,
        title: formatMessage(
          {
            id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.people_who_may_need_attention.title',
            defaultMessage:
              "{organizationName}'s employees who may need attention",
          },
          { organizationName }
        ),
        datakey: 'concerns',
        showRating: ratingRequired,
        shouldShowInfluence,
        shouldShowGoldStars,
        shouldShowHeadsUps,
      },
    ],
    [
      {
        type: CARD_TYPES.SCATTER_PLOT,
        title: formatMessage({
          id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.vs_ternure.title',
          defaultMessage: 'vs tenure',
        }),

        datakey: 'breakdowns',
        field: 'individual',
        metric: consts.PERFORMANCE_METRICS[1],
        reverse: true,
        shouldShowInfluence,
        shouldShowGoldStars,
        shouldShowHeadsUps,
        beforeContent: (
          <div>
            <FormattedMessage
              id="app.views.widgets.dashboards.takeaways_dashboard.individual.before_content"
              defaultMessage="
      There is no significant correlation between heads ups and tenure.
    "
            />
          </div>
        ),
      },
    ],
    [
      {
        type: CARD_TYPES.CANDLESTICK_CHART,
        datakey: 'breakdowns',
        field: 'function',
        color: colorConfig.heads_up,
        metric: consts.PERFORMANCE_METRICS[1],
        beforeContent: (
          <div>
            <FormattedMessage
              id="app.views.widgets.dashboards.takeaways_dashboard.function.before_content"
              defaultMessage="Hover on a candlestick to see its distribution details."
            />
          </div>
        ),
      },
    ],
    [
      {
        type: CARD_TYPES.BREAKDOWN_CHART,
        breakdownType: 'heads_ups',
        color: colorConfig.heads_up,
        tenureColor: colorConfig.tenure,
        title: formatMessage(
          {
            id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.average_heads_up_tenure.title',
            defaultMessage: 'Average heads ups and tenure by{space}',
          },
          { space: ' ' }
        ),
        datakey: 'breakdowns',
        beforeContent: (
          <div>
            <FormattedMessage
              id="app.views.widgets.dashboards.takeaways_dashboard.heads_up.before_content"
              defaultMessage="
      Keep in mind these averages when assessing specific individuals.
    "
            />
          </div>
        ),
      },
    ],
  ];
};

export const renderReccommendationsRows = ({ formatMessage }) => {
  return [
    [
      {
        id: 'whats-next-header',
        type: CARD_TYPES.HEADER,
        title: formatMessage({
          id: 'app.views.widgets.dashboards.takeaways_dashboard.rows.whats_next.title',
          defaultMessage: "What's next?",
        }),
        datakey: 'whats_next_header',
      },
    ],
    [
      {
        type: CARD_TYPES.SIMPLE_CARD,
        title: (
          <span>
            <FormattedMessage
              id="app.views.widgets.dashboards.takeaways_dashboard.rows.whats_next.title"
              defaultMessage="What's next?"
            />
          </span>
        ),
        datakey: 'whats_next',
      },
    ],
  ];
};
